// src/pages/dashboard/nanny/Dashboard.jsx
import { useState, useEffect } from 'react';
import { formatMoney, formatDateOnly } from '../../../lib/helpers';
import { 
  CheckCircle, Clock, MessageSquare, AlertCircle, TrendingUp, Users, DollarSign,
  Calendar, Star, Banknote, X
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/layout/ui/Card';
import Button from '../../../components/layout/ui/Button';
import { Link } from 'react-router-dom';
import RatingDisplay from '../../../components/common/RatingDisplay';
import AvailabilityToggle from '../../../components/common/AvailabilityToggle';
import BadgeResource from '../../../components/layout/ui/BadgeResource';
import { error, success } from '../../../components/common/Toast';
import CustomerDetailModal from '../../../components/customer/CustomerDetailModal';

// Import New Components

const statusConfig = {
  pending: { color: 'warning', label: 'Pending', icon: Clock },
  accepted: { color: 'success', label: 'Accepted', icon: CheckCircle },
  rejected: { color: 'danger', label: 'Rejected' },
  negotiating: { color: 'info', label: 'Negotiating', icon: MessageSquare },
};

// Customer Mini Card (used inside offers)
const CustomerMiniCard = ({ customer, onClick }) => {
  const photoUrl = customer?.profile_picture
    ? `${process.env.REACT_APP_IMAGE_URL}${customer.profile_picture}`
    : null;

  return (
    <div 
      className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl cursor-pointer hover:shadow-lg transition-all"
      onClick={onClick}
    >
      <div className="w-16 h-16 rounded-full bg-white/50 backdrop-blur border-4 border-white/60 overflow-hidden shadow-md">
        {photoUrl ? (
          <img src={photoUrl} alt={customer.full_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-black">
            {customer.full_name?.[0] || 'C'}
          </div>
        )}
      </div>
      <div>
        <h4 className="font-bold text-lg text-purple-900">{customer.full_name}</h4>
        <div className="flex items-center gap-2">
          <RatingDisplay rating={customer.rating || 0} size="sm" />
          {customer.is_vetted && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">Verified</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default function NannyDashboard() {
  const { user, api } = useAuth();
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({
    totalOffers: 0,
    pendingOffers: 0,
    activeContract: false,
    totalEarnings: 0,
    profileViews: null,
    workingSchedule: null
  });
  const [loading, setLoading] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'flexible',
    preferred_days: [],
    notes: ''
  });

  // Modal State
  const [customerModal, setCustomerModal] = useState({ open: false, customer: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [offersRes, contractRes, statsRes, scheduleRes] = await Promise.all([
        api.get('/offers/my-offers'),
        api.get('/contracts/current'),
        api.get(`/profile/${user?.profile?.id || user?.id}/stats`),
        api.get('/working-schedule')
      ]);

      const offersData = offersRes.data.data?.received || offersRes.data || [];
      const currentContract = contractRes.data.data || contractRes.data;
      const profileStats = statsRes.data;

      setOffers(offersData);

      setStats({
        totalOffers: offersData.length,
        pendingOffers: offersData.filter(o => o.status === 'pending').length,
        activeContract: !!currentContract,
        totalEarnings: offersData
          .filter(o => o.status === 'accepted')
          .reduce((sum, o) => sum + Number(o.amount), 0),
        profileViews: profileStats,
        workingSchedule: scheduleRes.data.data
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <BadgeResource variant={config.color} className="flex items-center gap-2 font-medium">
        {Icon && <Icon size={16} />}
        {config.label}
      </BadgeResource>
    );
  };

  if (loading) {
    return <div className="text-center py-20 text-muted">Loading your dashboard...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Welcome Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900">
          Welcome back, {user?.first_name}!
        </h1>
        <p><RatingDisplay rating={user?.rating} /></p>
        <p className="text-muted mt-2 text-lg">
          {user?.is_vetted ? (
            <span className="text-green-600 font-medium">Your profile is vetted and visible to employers</span>
          ) : (
            "Complete your profile to start receiving offers"
          )}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 text-center">
          <Banknote size={40} className="mx-auto text-secondary mb-3" />
          <p className="text-3xl font-bold text-secondary">{formatMoney(stats.totalEarnings)}</p>
          <p className="text-muted">Total Earnings (Accepted)</p>
        </Card>

        <Card className="p-6 text-center">
          <MessageSquare size={40} className="mx-auto text-primary mb-3" />
          <p className="text-3xl font-bold text-primary">{stats.totalOffers}</p>
          <p className="text-muted">Total Offers Received</p>
        </Card>

        <Card className="p-6 text-center">
          <Clock size={40} className="mx-auto text-orange-500 mb-3" />
          <p className="text-3xl font-bold text-orange-500">{stats.pendingOffers}</p>
          <p className="text-muted">Pending Review</p>
        </Card>

        <Card className="p-6 text-center">
          <Users size={40} className="mx-auto text-green-600 mb-3" />
          <p className="text-3xl font-bold text-green-600">
            {stats.activeContract ? 'Yes' : 'No'}
          </p>
          <p className="text-muted">Active Contract</p>
        </Card>
      </div>

      {/* Recent Offers */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Recent Offers</h2>
          <Link to="/dashboard/nanny/offers">
            <Button variant="outline">View All Offers</Button>
          </Link>
        </div>

        {offers.length === 0 ? (
          <Card className="text-center py-20">
            <AlertCircle size={80} className="mx-auto text-gray-300 mb-6" />
            <h3 className="text-2xl font-semibold mb-3">No offers yet</h3>
            <p className="text-muted text-lg max-w-md mx-auto">
              Once employers discover your profile, their offers will appear here.
            </p>
            {!user?.is_vetted && (
              <Link to="/dashboard/nanny/profile">
                <Button className="mt-6">Complete Your Profile to Get Offers</Button>
              </Link>
            )}
          </Card>
        ) : (
          <div className="space-y-6">
            {offers.slice(0, 5).map(offer => {
              const latestNegotiation = offer.negotiations?.slice(-1)[0];

              return (
                <Card key={offer.id} className="p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                    {/* Customer Card */}
                    <div 
                      className="w-full lg:w-auto"
                      onClick={() => setCustomerModal({ open: true, customer: offer.made_by })}
                    >
                      <CustomerMiniCard customer={offer.made_by} />
                    </div>

                    {/* Offer Details */}
                    <div className="flex-1 space-y-4">
                      <div className="text-right">
                        <p className="text-4xl font-black text-primary">
                          {formatMoney(offer.amount)}<span className="text-xl text-muted">/month</span>
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDateOnly(offer.created_at)}
                        </p>
                      </div>

                      {offer.conditions && (
                        <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                          <p className="font-bold text-blue-900 mb-2">Offer Conditions:</p>
                          <p className="text-blue-800 whitespace-pre-line">{offer.conditions}</p>
                        </div>
                      )}

                      {latestNegotiation && (
                        <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200">
                          <p className="text-sm font-medium text-purple-900">Latest Message:</p>
                          <p className="italic text-purple-800">"{latestNegotiation.comment}"</p>
                        </div>
                      )}
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-4 w-full lg:w-auto">
                      {getStatusBadge(offer.status)}
                      {offer.status === 'pending' && (
                        <div className="flex gap-3">
                          <Button size="lg" variant="outline" className="font-bold">
                            Negotiate
                          </Button>
                          <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                            Accept Offer
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Availability */}
        <Card className="p-8 text-center hover:shadow-lg transition-shadow">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-500 ${
                user?.is_active ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {user?.is_active ? (
                  <CheckCircle size={64} className="text-green-600" />
                ) : (
                  <X size={64} className="text-gray-400" />
                )}
              </div>
              {user?.is_active && (
                <div className="absolute inset-0 rounded-full animate-ping bg-green-400 opacity-20" />
              )}
            </div>

            <h3 className="text-2xl font-bold mt-6 mb-2">Availability Status</h3>
            <p className={`text-lg font-semibold mb-6 ${user?.is_active ? 'text-green-600' : 'text-gray-500'}`}>
              You are currently <span className="underline">{user?.is_active ? 'active' : 'inactive'}</span>
            </p>

            <AvailabilityToggle
              profileId={user?.profile?.id || user?.id}
              isActive={user?.is_active || false}
              onToggle={fetchData}
            />
          </div>
        </Card>

        {/* Profile Views */}
        {stats.profileViews ? (
          <Card className="p-8 text-center hover:shadow-lg transition-shadow">
            <TrendingUp size={48} className="mx-auto text-green-600 mb-4" />
            <div className="space-y-2">
              <p className="text-4xl font-black text-green-600">{stats.profileViews.today}</p>
              <p className="text-lg font-semibold">Employers viewed you today</p>
              <p className="text-sm text-muted">
                Total all time: <strong>{stats.profileViews.total}</strong>
              </p>
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center hover:shadow-lg transition-shadow">
            <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-muted">No views yet today</p>
          </Card>
        )}

        {/* Schedule */}
        {stats.workingSchedule ? (
          <Card className="p-8 hover:shadow-lg transition-shadow">
            <div className="text-center">
              <Calendar size={48} className="mx-auto text-primary mb-4" />
              <h3 className="text-xl font-bold mb-4">My Working Schedule</h3>
              <div className="bg-gray-50 rounded-xl p-5 text-left space-y-3">
                <p className="font-semibold text-lg">
                  {stats.workingSchedule.schedule_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </p>
                {stats.workingSchedule.preferred_days?.length > 0 && (
                  <p className="text-sm text-gray-600">
                    Days: <span className="font-medium">{stats.workingSchedule.preferred_days.join(', ')}</span>
                  </p>
                )}
                {stats.workingSchedule.notes && (
                  <p className="text-sm italic text-gray-500 mt-2">"{stats.workingSchedule.notes}"</p>
                )}
              </div>
              <Button variant="outline" className="mt-6" onClick={() => setScheduleModalOpen(true)}>
                Update Schedule
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center hover:shadow-lg transition-shadow">
            <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-muted">No schedule set</p>
          </Card>
        )}
      </div>

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        customer={customerModal.customer}
        isOpen={customerModal.open}
        onClose={() => setCustomerModal({ open: false, customer: null })}
      />

      {/* Schedule Modal */}
            {/* WORKING SCHEDULE MODAL */}
{scheduleModalOpen && (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setScheduleModalOpen(false)}>
    <div className="bg-white rounded-3xl p-10 max-w-2xl w-full max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
      <h2 className="text-3xl font-bold mb-8 text-center">Update Your Working Schedule</h2>

      <div className="space-y-6">
        <div>
          <label className="block text-lg font-medium mb-3">How many days can you work per week?</label>
          <select
            value={scheduleForm.schedule_type}
            onChange={(e) => {
              const val = e.target.value;
              setScheduleForm({ ...scheduleForm, schedule_type: val });
            }}
            className="w-full px-5 py-4 border-2 rounded-xl focus:ring-4 focus:ring-primary/20"
          >
            <option value="7_days_a_week">7 days a week</option>
            <option value="6_days_a_week">6 days a week</option>
            <option value="5_days_a_week">5 days a week (Most Common)</option>
            <option value="4_days_a_week">4 days a week</option>
            <option value="3_days_a_week">3 days a week</option>
            <option value="2_days_a_week">2 days a week</option>
            <option value="1_day_a_week">1 day a week</option>
            <option value="weekends_only">Weekends only</option>
            <option value="weekdays_only">Weekdays only</option>
            <option value="flexible">Flexible / Negotiable</option>
          </select>
        </div>

        {scheduleForm.schedule_type && !['7_days_a_week', 'weekdays_only', 'weekends_only', 'flexible'].includes(scheduleForm.schedule_type) && (
          <div>
            <p className="font-medium mb-3">Select your preferred days:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <label key={day} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleForm.preferred_days.includes(day)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setScheduleForm(prev => ({
                          ...prev,
                          preferred_days: [...prev.preferred_days, day]
                        }));
                      } else {
                        setScheduleForm(prev => ({
                          ...prev,
                          preferred_days: prev.preferred_days.filter(d => d !== day)
                        }));
                      }
                    }}
                    className="w-5 h-5 text-primary rounded focus:ring-primary"
                  />
                  <span className="text-lg">{day.slice(0,3)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-lg font-medium mb-3">Additional Notes (optional)</label>
          <textarea
            rows={4}
            value={scheduleForm.notes}
            onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
            placeholder="e.g. Only mornings, no overnights, prefer school runs only..."
            className="w-full px-5 py-4 border-2 rounded-xl focus:ring-4 focus:ring-primary/20"
          />
        </div>

        <div className="flex justify-end gap-4 pt-6">
  <Button
    variant="outline"
    onClick={() => setScheduleModalOpen(false)}
    disabled={loading}
  >
    Cancel
  </Button>

  <Button
    loading={loadingSchedule}
    disabled={loadingSchedule}
    onClick={async () => {
      setLoadingSchedule(true);
      try {
        await api.post('/working-schedule', scheduleForm);

        // Success toast using your custom function
        success('Working schedule updated successfully!');

        // Close modal and refresh data
        setScheduleModalOpen(false);
        fetchData();
      } catch (err) {
        // Error toast using your custom function
        error(err.response?.data?.message || 'Failed to save schedule');
      } finally {
        setLoadingSchedule(false);
      }
    }}
  >
    {loadingSchedule ? 'Saving...' : 'Save Schedule'}
  </Button>
</div>
      </div>
    </div>
  </div>
)}
    </div>
  );
}